import { useState, useCallback, useRef, useEffect } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * Format a SQL column name into a readable header.
 * e.g. "products.name" → "Product", "COALESCE(...)" → "Channel", "period" → "Period"
 */
function formatColumnHeader(col) {
  if (/products\.name/i.test(col)) return 'Product';
  if (/customers\.name/i.test(col)) return 'Customer';
  if (/customer_type/i.test(col)) return 'Customer Type';
  if (/COALESCE.*sale_channel/i.test(col)) return 'Channel';
  if (/COALESCE.*customer_type/i.test(col)) return 'Customer Type';
  if (col === 'period') return 'Period';
  if (col === 'revenue') return 'Revenue';
  if (col === 'quantity') return 'Quantity';
  // Fallback: strip table prefix, replace underscores, title case
  const clean = col.replace(/^\w+\./, '').replace(/_/g, ' ');
  return clean.charAt(0).toUpperCase() + clean.slice(1);
}

/**
 * Format a cell value for display.
 */
function formatCellValue(key, value) {
  if (value === null || value === undefined) return '-';
  if (key === 'revenue' || key === 'total_revenue') {
    return Number(value).toLocaleString('en-GH', { style: 'currency', currency: 'GHS', minimumFractionDigits: 0 });
  }
  if (key === 'period') {
    const d = new Date(value);
    if (!isNaN(d)) {
      return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    }
  }
  if (typeof value === 'number') {
    return value.toLocaleString();
  }
  return String(value);
}

/**
 * Build a human-readable summary from intent + params.
 */
function capitalize(s) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function buildSummary(intentId, params) {
  const ranking = params.ranking;
  const trend = params.trend;
  const breakdown = params.breakdown;

  if (intentId === 'performance_ranking' && ranking) {
    const dir = capitalize(ranking.direction || 'top');
    const limit = ranking.limit || 5;
    const dim = capitalize(ranking.dimension || 'product');
    return `${dir} ${limit} ${dim}s by Revenue`;
  }
  if (intentId === 'metric_trend' && trend) {
    const gran = capitalize(trend.granularity || 'month');
    return `${gran}ly Revenue Trend`;
  }
  if (intentId === 'metric_breakdown' && breakdown) {
    const dims = breakdown.dimensions || ['product'];
    const label = dims.map(capitalize).join(' by ');
    return `Revenue by ${label}`;
  }
  return 'Query Results';
}

/**
 * Format a date range for display.
 */
function formatDateRange(dateRange) {
  if (!dateRange) return '';
  const fmt = (iso) => {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  return `${fmt(dateRange.start_date)} – ${fmt(dateRange.end_date)}`;
}

/**
 * ResultCard — renders query results as an inline card with table.
 */
function ResultCard({ result }) {
  const { intent_id, confidence, params, date_range, data, row_count } = result;

  if (!data || data.length === 0) {
    return (
      <div className="query-result">
        <div className="result-summary">{buildSummary(intent_id, params)}</div>
        <div className="result-meta">{formatDateRange(date_range)}</div>
        <p style={{ color: '#888', fontStyle: 'italic', margin: '0.75rem 0 0' }}>No data found for this query.</p>
      </div>
    );
  }

  const columns = Object.keys(data[0]);

  return (
    <div className="query-result">
      <div className="result-summary">{buildSummary(intent_id, params)}</div>
      <div className="result-meta">
        <span>{formatDateRange(date_range)}</span>
        <span className="confidence-badge">{Math.round(confidence * 100)}% match</span>
      </div>
      <table className="result-table">
        <thead>
          <tr>
            {columns.map(col => (
              <th key={col}>{formatColumnHeader(col)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr key={i}>
              {columns.map(col => (
                <td key={col}>{formatCellValue(col, row[col])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="result-meta" style={{ marginTop: '0.5rem' }}>
        {row_count} row{row_count !== 1 ? 's' : ''} returned
      </div>
    </div>
  );
}

/**
 * AnalyticsChat — chat interface that posts to /analytics/query
 * and renders results inline as data tables.
 */
function AnalyticsChat() {
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Hi! Ask me about your sales data. Try: "top products by revenue" or "monthly sales trend"' }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = useCallback(async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setIsLoading(true);

    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setMessages(prev => [...prev, { role: 'assistant', content: 'Analyzing your query...', isThinking: true }]);

    try {
      const response = await fetch(`${API_BASE_URL}/analytics/query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: userMessage }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ detail: `HTTP ${response.status}` }));
        throw new Error(err.detail || `API error: ${response.status}`);
      }

      const data = await response.json();

      setMessages(prev => {
        const filtered = prev.filter(m => !m.isThinking);
        return [...filtered, { role: 'assistant', content: '', queryResult: data }];
      });

    } catch (err) {
      setMessages(prev => {
        const filtered = prev.filter(m => !m.isThinking);
        return [...filtered, { role: 'assistant', content: err.message, isError: true }];
      });
    } finally {
      setIsLoading(false);
    }
  }, [inputValue, isLoading]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="analytics-container">
      <div className="analytics-chat">
        <div className="chat-header">
          <h2>Sales Analytics</h2>
          <p>Ask questions about your sales data</p>
        </div>

        <div className="chat-messages">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`message ${msg.role} ${msg.isThinking ? 'thinking' : ''} ${msg.isError ? 'error' : ''}`}
            >
              <div className="message-content">
                {msg.queryResult ? (
                  <ResultCard result={msg.queryResult} />
                ) : (
                  msg.content
                )}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        <div className="chat-input">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask about your sales data..."
            disabled={isLoading}
          />
          <button
            onClick={sendMessage}
            disabled={isLoading || !inputValue.trim()}
          >
            {isLoading ? '...' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AnalyticsChat;
